
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import {
	EMAIL_REGEX,
	SNACKBAR_STATUS,
} from '@/constants/constants';
import {
	DEFAULT_SHOW_TEXT_COUNTER_AT,
	DEFAULT_TEXT_MAX_LENGTH,
	getTextMaxLengthRule,
} from '@/utils';
import BackButton from '@/components/base/BackButton.vue';
import { auth, db, functions } from '@/firebase';
import type { FirebaseUserCredential } from '@/firebase';
import _sortBy from 'lodash/sortBy';
import _trim from 'lodash/trim';
import AdminUserStatsCard from '@/components/AdminStats/AdminUserStats/AdminUserStatsCard.vue';
import { get$bindFirestoreOptions } from '@/mixins';
import { calcRealtimeBalanceInfo, DeviceReportUserSignUp, User } from '@run-diffusion/shared';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import { ROUTER } from '@/router/constants';

export default Vue.extend({
	name: 'AdminUsersManagement',
	metaInfo: {
		title: 'Users Management | Admin | RunDiffusion',
	},
	data () {
		return {
			DEFAULT_SHOW_TEXT_COUNTER_AT,
			DEFAULT_TEXT_MAX_LENGTH,

			resultUsers: [],
			resultUserBalanceAccount: null,
			isSigningInAsUser: false,
			isSettingBanUser: false,
			sendBanEmail: true,
			isChangingSsoEmail: false,
			newSsoEmail: '',
			searchEmail: null,
			loadingEmailSearch: false,

			directDeviceReportUserSignUp: null,
			allMatchingDeviceReportUserSignUps: [],
			allMatchingDeviceUsers: [],

			formValid: false,
			emailRules: [
				(v: string) => !!_trim(v) || 'Email is required',
				getTextMaxLengthRule(),
				v => EMAIL_REGEX.test(_trim(v)) || 'Incorrect email format',
			],
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		searchedUser () {
			return this.resultUsers && this.resultUsers.length ? this.resultUsers[0] : null;
		},
		isSearchedUserBanned () {
			// "Banned" state per product definition: activatedAt is null AND a deactivateReason is defined
			return !!(this.searchedUser && !this.searchedUser.activatedAt && this.searchedUser.deactivateReason);
		},
		searchedUserTokensToRemove () {
			// Combined paid + free Runnit tokens (in hundredths) a ban will remove
			const { realtimeRunnitTokens, realtimeFreeRunnitTokens } = calcRealtimeBalanceInfo(this.resultUserBalanceAccount);
			return (realtimeRunnitTokens || 0) + (realtimeFreeRunnitTokens || 0);
		},
		searchedUserFundsToRemove () {
			// Open-Source Funds = balance + recurring balance (in cents) a ban will remove
			const { realtimeBalance, realtimeRecurringBalance } = calcRealtimeBalanceInfo(this.resultUserBalanceAccount);
			return (realtimeBalance || 0) + (realtimeRecurringBalance || 0);
		},
	},
	created () {
		const routeUserEmail: string = _trim(this.$route.query.userEmail).toLowerCase();
		if (routeUserEmail) {
			this.searchEmail = routeUserEmail;
			this.$nextTick(() => {
				this.doEmailSearch();
			});
		}
		this.$nextTick(() => {
			this.$refs.searchEmail?.focus();
		});
	},
	watch: {
		resultUsers: {
			immediate: true,
			async handler (newVal: User[], oldVal: User[]) {
				const newUserId: string = this._get(newVal, '[0].id') || null
				if (newUserId) {
					// deviceReportUserSignUps
					const directDeviceReportUserSignUpRef = db.collection(`deviceReportUserSignUps`).doc(newUserId);
					await this.$bind(
						'directDeviceReportUserSignUp',
						directDeviceReportUserSignUpRef,
						get$bindFirestoreOptions(),
					);
					// balanceAccount (to preview the Tokens & Open-Source Funds a ban would remove)
					const balanceAccountRef = db.doc(`balanceAccounts/${newUserId}`);
					await this.$bind(
						'resultUserBalanceAccount',
						balanceAccountRef,
						get$bindFirestoreOptions(),
					);
				}
			},
		},
		directDeviceReportUserSignUp: {
			immediate: true,
			async handler (newVal: DeviceReportUserSignUp, oldVal: DeviceReportUserSignUp) {
				if (newVal && newVal.fingerprint && newVal.fingerprint.visitorId && newVal !== oldVal) {
					const allMatchingDeviceReportUserSignUpsRef = db.collection(`deviceReportUserSignUps`)
						.where('fingerprint.visitorId', '==', newVal.fingerprint.visitorId)
						.limit(500);
					await this.$bind(
						'allMatchingDeviceReportUserSignUps',
						allMatchingDeviceReportUserSignUpsRef,
						get$bindFirestoreOptions(),
					);
				}
			},
		},
		allMatchingDeviceReportUserSignUps: {
			immediate: true,
			async handler (newVal: DeviceReportUserSignUp[], oldVal: DeviceReportUserSignUp[]) {
				if (newVal && newVal.length) {
					const startingUserId: string = this.directDeviceReportUserSignUp.userId;
					const users: User[] = _sortBy(
						await Promise.all(newVal.map(async (deviceReportUserSignUp: DeviceReportUserSignUp) => {
							const userRef = db.doc(`users/${deviceReportUserSignUp.userId}`);
							const userSnapshot = await userRef.get();
							return {
								...userSnapshot.data(),
								get id () { return userSnapshot.id },
							} as User;
						})),
						'email',
					);

					if (
						this.directDeviceReportUserSignUp &&
						startingUserId === this.directDeviceReportUserSignUp.userId &&
						this.directDeviceReportUserSignUp.fingerprint.visitorId === newVal[0].fingerprint.visitorId
					) {
						// Make sure it didn't change while fetching the data
						this.allMatchingDeviceUsers = users;
					}
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'setAdminUserProxyTokens',
		]),
		goBack () {
			if (window.history.length > 2) {
				this.$router.back();
			} else {
				const hasCurrentSessionId: boolean = !!(this._get(this.toolbar, 'session.id'));

				if (hasCurrentSessionId) {
					this.routerPush(this.$route, this.$router, { name: ROUTER.LAUNCH, params: { sessionId: this._get(this.toolbar, 'session.id') } });
				} else {
					this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
				}
			}
		},
		async doEmailSearch () {
			if (!this.formValid) return;
			const trimmedEmail: string = _trim(this.searchEmail).toLowerCase();
			if (!trimmedEmail) return;

			try {
				this.loadingEmailSearch = true;
				if (this._firestoreUnbinds['directDeviceReportUserSignUp']) {
					this.$unbind('directDeviceReportUserSignUp');
				}
				if (this._firestoreUnbinds['allMatchingDeviceReportUserSignUps']) {
					this.$unbind('allMatchingDeviceReportUserSignUps');
				}
				if (this._firestoreUnbinds['resultUserBalanceAccount']) {
					this.$unbind('resultUserBalanceAccount');
				}
				this.allMatchingDeviceUsers = [];

				const usersRef = db.collection(`users`)
					.where('email', '==', trimmedEmail)
					.limit(1);
				await this.$bind(
					'resultUsers',
					usersRef,
					get$bindFirestoreOptions(),
				);
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error searching users by email',
					show: true,
				});
			} finally {
				this.loadingEmailSearch = false;
			}
		},
		async doAdminSignInAsUser (userId: string) {
			const onError: Function = (e) => {
				console.error('Error signing in as user: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error signing in as user',
					show: true,
				});
			};

			try {
				this.isSigningInAsUser = true;

				const functionRef = functions
					.httpsCallable('adminSignInAsUser');
				const { success, proxyAdminToken, proxyToken, userEmail } = (await functionRef({
					userId,
				})).data;
				if (!success) {
					onError(new Error('adminSignInAsUser not successful'));
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Success! Signed in as user: ${userEmail}`,
						show: true,
					});

					// Timeout to give snackbar a while to show
					setTimeout(async () => {
						// Store admin user custom token and id token in local storage
						const proxyAdminIdToken: string = await auth.currentUser.getIdToken(true);
						this.setAdminUserProxyTokens({
							proxyAdminToken,
							proxyAdminIdToken,
						});

						// Sign in to new user
						await auth.signOut();
						const userCredential: FirebaseUserCredential = await auth.signInWithCustomToken(proxyToken);
						await this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
						window.location.reload();
					}, 1000);
				}
			} catch (e) {
				onError(e);
			} finally {
				this.isSigningInAsUser = false;
			}
		},
		async doChangeSsoUserEmail (userId: string, newEmail: string, closeAndResetCallback: Function) {
			// Errors are surfaced verbatim from the callable: the "email already in use"
			// case carries the conflicting account's ID and creation date, which is what
			// support needs to decide whether it's a safe cleanup or a real account.
			let succeeded: boolean = false;
			try {
				this.isChangingSsoEmail = true;

				const functionRef = functions
					.httpsCallable('changeSsoUserEmail');
				const { success, message } = (await functionRef({
					userId,
					newEmail,
				})).data;
				succeeded = !!success;

				this.updateSnackbar({
					status: success ? SNACKBAR_STATUS.SUCCESS : SNACKBAR_STATUS.ERROR,
					message: message || (success
						? `Login email changed to ${newEmail}`
						: 'Error changing login email'),
					show: true,
					timeout: 20000, // long: the message tells the operator what to do next
				});

				if (success) {
					this.newSsoEmail = '';
					await this.doEmailSearch();
				}
			} catch (e) {
				console.error('Error changing SSO login email: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error changing login email',
					show: true,
				});
			} finally {
				this.isChangingSsoEmail = false;
				if (closeAndResetCallback) {
					closeAndResetCallback();
				}
			}
		},
		async doSetBanUser (userId: string, ban: boolean, sendEmail: boolean, closeAndResetCallback: Function) {
			const actioning: string = ban ? 'banning' : 'un-banning';
			try {
				this.isSettingBanUser = true;

				const functionRef = functions
					.httpsCallable('setBanUser');
				const { success, message } = (await functionRef({
					userId,
					ban,
					sendEmail,
				})).data;

				this.updateSnackbar({
					status: success ? SNACKBAR_STATUS.SUCCESS : SNACKBAR_STATUS.ERROR,
					message: message || (success
						? `User has been ${ban ? 'banned' : 'un-banned'}`
						: `Error ${actioning} user`),
					show: true,
				});

				if (success) {
					// Refresh the searched user so the admin sees the updated state
					await this.doEmailSearch();
				}
			} catch (e) {
				console.error(`Error ${actioning} user: `, e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error ${actioning} user`,
					show: true,
				});
			} finally {
				this.isSettingBanUser = false;
				this.sendBanEmail = true; // reset toggle to its default (on) for the next action
				if (closeAndResetCallback) {
					closeAndResetCallback();
				}
			}
		},
	},
	components: {
		ConfirmDialog,
		DoubleConfirmDangerDialog,
		BaseToggle,
		AdminUserStatsCard,
		BackButton,
	},
});
